import React, { useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withI18next } from 'lib/i18n/withI18next'
import compose from 'recompose/compose'
import ImageCarouselAutoPlayVideo from 'components/pdp/carousel-images/image-carousel/image-carousel-auto-play-video/ImageCarouselAutoPlayVideo'
import Swiper from 'react-id-swiper'
import ResponsiveImage from 'components/images/responsive-image'
import clsx from 'clsx'
import { segmentProductVideoViewedEvent } from 'components/product/product-segment/productSegment'
import segmentProductImageGalleryViewed from 'components/pdp/carousel-images/carousel-images-segment/carouselImagesSegment'
import useIntersection from 'components/custom-hooks/useIntersection'

const ProductItemSwipingGallery = ({ images_array, eventData }) => {
  const imgProps = {
    alt: 'name',
    bigDesktop: '18vw',
    desktop: '18vw',
    imgRatio: 1.33,
    laptop: '18vw',
    phone: '45vw',
    tablet: '18vw',
  }

  const swiperRef = useRef(null)
  const heroImgRef = useRef(null)
  const lastIndexRef = useRef(images_array.length - 1)
  const isIntersectingImg = useIntersection(heroImgRef, {
    once: false,
  })

  const handleSlideChange = useCallback(() => {
    const { realIndex } = swiperRef.current.swiper
    const media = images_array?.[realIndex]?.type
    const isVideo = media === 'video'

    if (!isVideo) {
      segmentProductImageGalleryViewed({
        ...eventData,
        image_type: media,
      })
    }
  }, [images_array, eventData])

  const goToSlide = (index) => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideTo(index)
    }
  }

  useEffect(() => {
    if (isIntersectingImg) {
      segmentProductImageGalleryViewed({
        ...eventData,
        image_type: 'jpg',
      })
    }
  }, [isIntersectingImg, eventData])

  useEffect(() => {
    lastIndexRef.current = images_array.length - 1
    goToSlide(0)
  }, [images_array])

  const sliderOptions = {
    slidesPerView: 1,
    loop: false,
    spaceBetween: 0,
    shouldSwiperUpdate: true,
    noSwiping: false,
    touchRatio: 0.6,
    threshold: 25,
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets',
      clickable: true,
    },
    on: {
      realIndexChange: () => handleSlideChange(),
      touchEnd: () => {
        const { activeIndex, touches } = swiperRef?.current?.swiper || {}
        const isLastSlide = activeIndex === lastIndexRef.current
        const isSwipeRight = touches.diff < -200

        if (isLastSlide && isSwipeRight) {
          setTimeout(() => {
            goToSlide(0)
          }, 100)
        }
      },
    },
  }

  return (
    <Swiper {...sliderOptions} ref={swiperRef}>
      {images_array.map((gallery, idx) => {
        if (gallery?.type === 'video')
          return (
            <div key={idx}>
              <ImageCarouselAutoPlayVideo
                loop
                autoPlay
                poster={gallery?.full_video_cover_url}
                src={gallery?.full_video_url}
                className="product-image__video"
                customSegmentTracking={() => {
                  segmentProductVideoViewedEvent({
                    ...eventData,
                  })
                }}
              />
            </div>
          )

        return (
          <div
            key={idx}
            className="product-image__cover"
            ref={idx === 0 ? heroImgRef : null}
          >
            <ResponsiveImage
              className={clsx({
                'product-image__italist-product': true,
              })}
              {...imgProps}
              src={gallery?.full_path}
            />
          </div>
        )
      })}
    </Swiper>
  )
}

ProductItemSwipingGallery.defaultProps = {
  images_array: [],
  eventData: {},
}

ProductItemSwipingGallery.propTypes = {
  images_array: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string })),
  eventData: PropTypes.shape({}),
}

export default compose(withI18next())(ProductItemSwipingGallery)
